import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Draggable, GridsterConfig, GridsterItemComponent, PushDirections, Resizable } from 'angular-gridster2';
import { I18NextPipe } from 'angular-i18next';
// import { ResizedEvent } from 'angular-resize-event';
import { ResizedEvent } from 'angular-resize-event-package';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DialogDeleteComponent } from 'src/app/common/dialogs/dialog-delete/dialog-delete.component';
import { isDashboardEditable } from 'src/app/dashboards/dashboard-helpers/helpers';
import { getActiveDashboard, getActiveTabWidgets, State } from 'src/app/dashboards/state';
import { getUserInfoState } from 'src/app/dashboards/state/index';
import { AppEventType } from 'src/app/enums';
import { dashboard, gridItem, gridItemExt } from 'src/app/models';
import { DashboardService } from 'src/app/services/services/dashboard.service';
import { EventQueueService } from 'src/app/services/services/EventQueueService';
import * as actions from './../state/actions';

interface Options extends GridsterConfig {
  draggable: Draggable;
  resizable: Resizable;
  pushDirections: PushDirections;
  minCols: 24;
  maxCols: 24;
  minRows: 10;
  maxRows: 1000;
  fixedRowHeight: 60;
  setGridSize: true;
  mobileBreakpoint: 0;
  gridType: 'scrollVertical';
  margin: 10
}

@Component({
    selector: 'app-dashboard-grid',
    templateUrl: './dashboard-grid.component.html',
    styleUrls: ['./dashboard-grid.component.scss'],
    standalone: false
})
export class DashboardGridComponent implements OnInit {
  private ngUnsubscribe = new Subject<void>();
  gridItems$: Observable<gridItem[]>;
  userInfo$: Observable<any>;
  getActiveDashboard$: Observable<dashboard>;
  gridItems: gridItemExt[] = [];
  options: Options;
  restrictedDataWidgets: number[] = [];
  isSelectedDashboardOwned:boolean;
  clearDashboardRequested: boolean = false;
  allowUserToEditOthersDashboards: boolean; // property per user
  isRoot: boolean;
  isPartner: boolean;
  isPublic: boolean;
  isActiveDashboardEditable: boolean;
  statisticsPageAccess: boolean = false;
  isReadOnly: boolean = false;

  constructor(private store: Store<State>, public dialog: MatDialog, private i18nextPipe: I18NextPipe, private logger: NGXLogger, private eventQueue: EventQueueService,
    private dashboardService: DashboardService) {

    // Should be placed bedore the subscribtion for the active dashboard
    this.userInfo$ = this.store.select(getUserInfoState);
    this.userInfo$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(info => {
      this.isRoot = info?.userProperties?.isRoot;
      this.isPartner = info?.userProperties?.isPartner;
      this.allowUserToEditOthersDashboards = info?.userProperties?.allowToEditOtherUsersDashboards;
      this.isReadOnly = info?.userProperties?.isDashboardsReadOnly;
    });

    this.getActiveDashboard$ = this.store.select(getActiveDashboard);
    this.getActiveDashboard$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(d => {
      d && (this.isActiveDashboardEditable = isDashboardEditable(d, this.isRoot, this.isPartner, this.allowUserToEditOthersDashboards, this.isReadOnly));
    });

    this.gridItems$ = this.store.select(getActiveTabWidgets);
    this.gridItems$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(gridItems => {
      this.gridItems = gridItems ? JSON.parse(JSON.stringify(gridItems)) : [];
      this.gridItems = this.gridItems.map(i => ({
        ...i,
        minItemRows: i.type == 3 ? 3 : i.minItemRows,
        minItemCols: i.type == 3 ? 3 : i.minItemCols,
        resizeEnabled: this.isActiveDashboardEditable,
        dragEnabled: this.isActiveDashboardEditable
      }))
    });

    dashboardService.getStatisticsPageAccess().subscribe(value => this.statisticsPageAccess = value);
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.logger.trace('init DashboardGridComponent');
    this.options = {
      minCols: 24,
      maxCols: 24,
      minRows: 10,
      maxRows: 1000,
      fixedRowHeight: 60,
      setGridSize: true,
      pushItems: false,
      swap: true,
      allowMultiLayer: false,
      mobileBreakpoint: 0,
      gridType: 'scrollVertical',
      margin: 10,
      minItemRows: 4,
      minItemCols: 4,
      itemChangeCallback: this.itemChange.bind(this),
      itemResizeCallback: this.itemChange.bind(this),
      draggable: {
        enabled: true
      },
      resizable: {
        enabled: true
      },
      pushDirections: {
        north: true, east: true, south: true, west: true
      }
    };
    this.eventQueue.on(AppEventType.DashboardsCancelEvent).subscribe(event => {
      this.clearDashboardRequested = true;
      setTimeout(() => { this.clearDashboardRequested = false }, 2000);
    });
  };

  trackBy(index: number, item: gridItem): number {
    return item.id;
  }

  public itemChange(item: gridItem, itemComponent: GridsterItemComponent) {
    this.logger.trace('DashboardGridComponent:itemChange event');

    this.gridItems$.pipe(take(1)).subscribe(w => {
      let storeItem: gridItem;
      storeItem = w.find(i => i.id === item.id);
      if (item.rows == storeItem?.rows && item.cols == storeItem?.cols && item.x == storeItem?.x && item.y == storeItem?.y) return;
      this.store.dispatch(actions.updateWidget({ gridItem: item }));
    });

  }

  removeItem($event, item) {
    this.logger.trace('DashboardGridComponent:removeItem event');
    const dialogRef = this.dialog.open(DialogDeleteComponent, {
      disableClose: true,
      data: {
        component: this.i18nextPipe.transform('dashboard:widget')
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.logger.trace('DashboardGridComponent:removeItem',item);
        this.store.dispatch(actions.removeWidget({ gridItem: item }));
      }
    });
  }

  onResized(event: ResizedEvent) {
    if(this.options.api){
        this.options.api.resize();
    }
  }

  onRestrictedData(event, item: gridItem) {
    if (event && this.restrictedDataWidgets.indexOf(item.id) < 0) {
      this.restrictedDataWidgets.push(item.id);
    } else if (!event) {
      this.restrictedDataWidgets = this.restrictedDataWidgets.filter((id) => item.id != id)
    }
  }
}
